import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { getFormularioSecretariaMunicipalAgriculturaByUUID } from 'api/forms/pacote_formularios.api';
import ContainerPainel from 'components/container_painel';
import { StepsVisibleForm } from 'components/forms/steps-visible-form';
import { Loading } from 'components/login';
import { useAuth } from 'contexts/auth/auth_context';
import { UserPermissionEnum } from 'enum/userPermission.enum';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { useFormularioSecretariaMunicipalAgriculturaStore } from 'stores/forms/formularioAgricultura.store';
import Step1View from './steps/step1_view';
import Step2View from './steps/step2_view';
import Step3View from './steps/step3_view';
import Step4View from './steps/step4_view';
import Step5View from './steps/step5_view';
import Step6View from './steps/step6_view';
import Step7View from './steps/step7_view';

export const getFormulario = async (
  uuid: string | undefined,
  setHasError: (v: boolean) => void,
  setErrorMessage: (s: string) => void,
  setFormulario: (f: any) => void,
) => {
  try {
    setHasError(false);
    setErrorMessage('');

    if (!uuid) {
      setHasError(true);
      setErrorMessage('UUID do formulário não encontrado');
      return;
    }

    const response = await getFormularioSecretariaMunicipalAgriculturaByUUID(uuid);

    if (!response?.dados) {
      setHasError(true);
      setErrorMessage('Município sem informação enviada para visualização pelo Gerente Estadual');
      return;
    }

    // Verificar se há dados preenchidos
    const temDadosPreenchidos = Object.values(response.dados).some(value =>
      value !== null && value !== undefined && value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
    );

    if (!temDadosPreenchidos) {
      setHasError(true);
      setErrorMessage('Nenhum dado preenchido disponível para este município');
      return;
    }

    setFormulario(response.dados);
  } catch (error) {
    // Keep behavior consistent with previous implementation
    // eslint-disable-next-line no-console
    console.error('Erro ao buscar formulário:', error);
    setHasError(true);
    setErrorMessage('Município sem informação enviada para visualização pelo Gerente Estadual');
  }
};

const FormularioSecretariaMunicipalAgriculturaVisualizar: React.FC = () => {
  const { uuid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const usuariosPermitidos = [
    UserPermissionEnum.STATE_MANAGER,
    UserPermissionEnum.MUNICIPAL_MANAGER,
    UserPermissionEnum.ADMIN,
  ];

  const isUsuarioPermitido = usuariosPermitidos.includes(
    user?.permission.name as UserPermissionEnum,
  );

  const { formulario, setFormulario } =
    useFormularioSecretariaMunicipalAgriculturaStore();



  useEffect(() => {
    if (uuid) {
      // use the exported helper so it can be unit-tested
      // pass state setters as arguments
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getFormulario(uuid, setHasError, setErrorMessage, setFormulario);
    }

    // Cleanup ao sair da tela
    return () => {
      setFormulario(null);
    };
  }, [uuid]);

  const renderStepContent = () => {
    if (hasError) {
      return (
        <Box mt={3} justifyContent="center" display="flex" >
          <Alert sx={{ maxWidth: 600 }} severity="warning" >
            <Typography variant="body1">
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      );
    }

    if (!formulario) {
      return (
        <Box justifyContent="center" display="flex">
          <Loading />
        </Box>
      );
    }

    // Renderizar o step atual
    switch (currentStep) {
      case 1: return <Step1View />;
      case 2: return <Step2View />;
      case 3: return <Step6View />;
      case 4: return <Step5View />;
      case 5: return <Step4View />;
      case 6: return <Step7View />;
      case 7: return <Step3View />;
      default: return <Step1View />;
    }
  };


  return (
    <ContainerPainel
      pagina='Formulário da Secretaria Municipal de Agricultura'
      showBackButton={true}
    >
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '24px',
          lineHeight: '133%',
          letterSpacing: '0px',
          textAlign: 'center',
          verticalAlign: 'middle',
          mt: '63px',
          mb: '32px',
        }}
      >
        Visualizar Formulário da Secretaria Municipal de Agricultura
      </Typography>

      {isUsuarioPermitido ? (
        <Stack marginX={'4rem'} paddingTop={'2rem'}>
          <StepsVisibleForm
            activeStep={currentStep}
            steps={[
              'INF. GERAIS',
              'INF. COMPLEMENTARES',
              'SERVIDORES',
              'ESTRUTURA',
              'SERVIÇOS PRESTADOS',
              'PROG. E ASSIST. TÉC.',
              'ÓRGÃOS ESTADUAIS',
            ]}
            onStepChange={setCurrentStep}
          />

          <Box mt={2}>{renderStepContent()}</Box>

          {/* Navegação entre abas */}
          {!hasError && formulario && (
            <Box display="flex" justifyContent="center" mt={4} mb={2}>
              <Button
                variant="text"
                onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
                sx={{ fontWeight: 600, letterSpacing: '0.5px' }}
              >
                VOLTAR
              </Button>
            </Box>
          )}
        </Stack>
      ) : (
        <Stack
          sx={{
            margin: '2rem',
            marginX: '4rem',

            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: '#FFF',
            borderRadius: '1rem',
            padding: '2rem',
            paddingTop: '2rem',
          }}
        >
          <Typography variant="h6" color="error">
            Acesso Negado
          </Typography>
          <Typography>
            Você não tem permissão para visualizar este formulário
          </Typography>
          <Button
            onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
            variant="contained"

          >
            Voltar
          </Button>
        </Stack>
      )}
    </ContainerPainel>
  );
};

export default FormularioSecretariaMunicipalAgriculturaVisualizar;
